/**
 * API Helpers
 *
 * This utility file provides standardized functions for API operations,
 * Redux state access, and form data formatting to ensure consistent
 * communication between frontend and backend.
 */
import axiosInstance from './axios-auth';
import clientEnv from '../env';
import { validateApiResponseStructure } from './response-validator';
/**
 * Standardizes API response handling to extract data consistently
 * regardless of response format from various API endpoints
 *
 * Expected format: { success: boolean, message?: string, data?: any, error?: object }
 */
export const normalizeApiResponse = (response) => {
    // Validate response structure in development
    if (process.env.NODE_ENV === 'development') {
        validateApiResponse(response);
    }
    // Handle different response formats
    if (response?.data?.data !== undefined) {
        // Format: { data: { success: true, data: {...} } }
        return response.data.data;
    }
    else if (response?.data?.success && response.data.data !== undefined) {
        // Format: { data: { success: true, data: {...} } }
        return response.data.data;
    }
    else if (response?.data && !response.data.success && response.data.data !== undefined) {
        // Error response with data field
        return response.data.data;
    }
    else if (response?.data) {
        // Format: { data: {...} } - might be direct data or formatted response
        // If it has success field, it's a formatted response without data field
        if (response.data.success !== undefined && response.data.data === undefined) {
            // Formatted response but no data field - return the whole object
            return response.data;
        }
        return response.data;
    }
    else {
        // Fallback for unexpected formats
        if (process.env.NODE_ENV === 'development') {
            console.warn('Unexpected API response format:', response);
        }
        return response;
    }
};
/**
 * Validates API response format in development mode
 * Logs warnings for non-standard responses
 */
const validateApiResponse = (response) => {
    if (!response?.data) {
        console.warn('API response missing data field:', response);
        return;
    }
    const responseData = response.data;
    // Use response validator for structured validation
    const validation = validateApiResponseStructure(responseData);
    if (!validation.valid) {
        console.warn('API response validation failed:', {
            errors: validation.errors,
            status: response.status,
            url: response.config?.url,
            responseData
        });
        return;
    }
    // Additional checks
    if (typeof responseData === 'object' && 'success' in responseData) {
        // Check if success matches status code
        const isSuccess = response.status >= 200 && response.status < 400;
        if (responseData.success !== isSuccess) {
            console.warn('API response success field does not match status code:', {
                success: responseData.success,
                status: response.status,
                url: response.config?.url
            });
        }
    }
};
/**
 * Ensures that all required fields have valid values
 * and converts fields to the expected types
 */
export const sanitizeApiPayload = (data, defaultValues = {}) => {
    // Create a copy to avoid modifying the original
    const sanitized = { ...defaultValues, ...data };
    // Remove undefined or null values
    Object.keys(sanitized).forEach(key => {
        if (sanitized[key] === undefined || sanitized[key] === null) {
            delete sanitized[key];
        }
    });
    return sanitized;
};
/**
 * Converts form data to API payload format
 * Handles specific field transformations like subjects
 */
export const formToApiPayload = (formData) => {
    const payload = { ...formData };
    // Convert subjects from string to array if needed
    if (typeof payload.subjects === 'string') {
        payload.subjects = payload.subjects
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }
    // Convert hourly_rate to number if it's a string
    if (typeof payload.hourly_rate === 'string') {
        payload.hourly_rate = parseFloat(payload.hourly_rate) || 0;
    }
    // Only include password if it's not empty
    if (payload.password === '') {
        delete payload.password;
    }
    // Remove form-only fields that shouldn't be sent to API
    delete payload.confirm_password;
    return payload;
};
/**
 * Gets a selector function for accessing data in Redux state
 * This helps ensure all components use the correct property paths
 */
export const createReduxSelector = (selector, defaultValue) => {
    return (state) => {
        try {
            const result = selector(state);
            return result !== undefined && result !== null ? result : defaultValue;
        }
        catch (error) {
            console.warn('Error accessing Redux state:', error);
            return defaultValue;
        }
    };
};
/**
 * Creates a selector for safely accessing nested props in Redux state
 */
export const createPropSelector = (stateSelector, prop, defaultValue) => {
    return (state) => {
        try {
            const obj = stateSelector(state);
            if (obj && typeof obj === 'object' && prop in obj) {
                return obj[prop];
            }
            return defaultValue;
        }
        catch (error) {
            console.warn(`Error accessing prop ${String(prop)} in Redux state:`, error);
            return defaultValue;
        }
    };
};
/**
 * Default API response error handler
 */
export const handleApiError = (error) => {
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    else if (error?.response?.data?.error) {
        return error.response.data.error;
    }
    else if (error?.message) {
        return error.message;
    }
    return 'An unexpected error occurred';
};
/**
 * Check if the API is reachable
 * @returns A promise that resolves to true if the API is reachable, false otherwise
 */
export const checkApiHealth = async () => {
    try {
        const response = await axiosInstance.get('/health', {
            timeout: 5000,
            headers: {
                'Accept': 'application/json'
            }
        });
        if (response.status === 200) {
            return {
                isHealthy: true,
                message: 'API is healthy and responding',
                details: response.data
            };
        }
        else {
            return {
                isHealthy: false,
                message: `API returned unexpected status: ${response.status}`,
                details: response.data
            };
        }
    }
    catch (error) {
        return {
            isHealthy: false,
            message: error.message || 'Failed to connect to API',
            details: {
                error: error.toString(),
                code: error.code,
                response: error.response?.data
            }
        };
    }
};
/**
 * Diagnose and attempt to fix common API connection issues
 */
export const diagnoseApiConnection = async () => {
    // Check API health first
    const healthCheck = await checkApiHealth();
    if (healthCheck.isHealthy) {
        return true;
    }
    // Try to diagnose the issue
    console.warn('❌ API connection issue:', healthCheck.message);
    // Check if running in development mode
    if (clientEnv.NODE_ENV === 'development') {
        // Check if localhost is being used
        if (axiosInstance.defaults.baseURL?.includes('localhost')) {
        }
        // Check for CORS issues
        if (healthCheck.message.includes('Network Error') || healthCheck.message.includes('CORS')) {
        }
        // Check for authentication issues
        if (healthCheck.details?.response?.status === 401 || healthCheck.details?.response?.status === 403) {
            // Clear tokens as they might be invalid
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
        }
    }
    return false;
};
/**
 * Try to refresh authentication if there are API issues
 */
export const refreshAuthentication = async () => {
    try {
        // Try to get a fresh session from Supabase
        const { data } = await axiosInstance.post('/auth/refresh-token');
        if (data && data.token) {
            // Store new token
            localStorage.setItem('access_token', data.token);
            if (data.refreshToken) {
                localStorage.setItem('refresh_token', data.refreshToken);
            }
            return true;
        }
        return false;
    }
    catch (error) {
        console.error('Failed to refresh authentication:', error);
        return false;
    }
};
